import * as React from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";

import { cn } from "@/lib/utils";

/**
 * Łapka (`grab`) musi być widoczna OD RAZU po najechaniu kursorem na Thumba —
 * a nie dopiero po `pointerdown`. Wcześniejsza implementacja przełączała
 * kursor w handlerach pointer-down/up, przez co przy zwykłym hoverze user
 * widział domyślną strzałkę. Teraz `cursor: grab` jest stałym stylem
 * Thumba (działa niezależnie od interakcji), a `:active` w CSS przełącza go
 * na `grabbing` w trakcie przeciągania.
 */
const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root>
>(({ className, ...props }, ref) => (
  <SliderPrimitive.Root
    ref={ref}
    className={cn("relative flex w-full touch-none select-none items-center", className)}
    {...props}
  >
    <SliderPrimitive.Track className="relative h-1.5 w-full grow cursor-pointer overflow-hidden rounded-full bg-primary/20">
      <SliderPrimitive.Range className="absolute h-full bg-primary" />
    </SliderPrimitive.Track>
    <SliderPrimitive.Thumb
      data-slider-thumb="true"
      style={{ cursor: "grab" }}
      className={cn(
        "relative block h-6 w-6 cursor-grab rounded-full border-2 border-primary bg-background shadow-md transition-transform after:absolute after:-inset-1.5 after:cursor-grab after:rounded-full after:content-['']",
        "hover:scale-110 hover:cursor-grab focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "active:scale-110 active:cursor-grabbing disabled:pointer-events-none disabled:opacity-50",
        // Inline-priority cursor — niektóre globalne style (preflight, base
        // layer w styles.css) potrafią nadpisać klasę Tailwinda, więc używamy
        // bezpośrednio CSS z `!important` przez arbitrary value.
        "[cursor:grab!important] active:[cursor:grabbing!important]",
      )}
    />
  </SliderPrimitive.Root>
));
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
